import { carCardPath, carPictureFileName } from '../../domain/cars/carShare';
import { appShareCardFileName, appShareCardPath } from '../../domain/share/appShare';

/**
 * Load a rendered share card as a `File`, ready for the share sheet.
 *
 * One module for both cards, because the mechanism is identical and contains
 * nothing about either: a same-origin fetch with an abort timeout, wrapped in a
 * `File` with a name and a MIME type. What differs is the path and the name, and
 * both of those are decided in `domain/` — this module only asks.
 *
 * `fetch`, the timeout and `File` arrive as defaulted parameters — the repo's
 * rule for anything touching a browser API, and what makes this testable with no
 * network and no share sheet.
 *
 * Same-origin, so the service worker's stale-while-revalidate applies and no
 * CORS is involved. Nothing here is PRECACHED — images never are — so the first
 * share fetches over the network, and offline it throws and the caller falls
 * back to the link.
 */

const REQUEST_TIMEOUT_MS = 10000;

export async function fetchShareImageFile(
    { url, fileName, type = 'image/jpeg' },
    {
        fetchImpl = globalThis.fetch,
        timeoutMs = REQUEST_TIMEOUT_MS,
        FileImpl = globalThis.File,
    } = {},
) {
    if (typeof fetchImpl !== 'function' || typeof FileImpl !== 'function') {
        throw new Error('This environment cannot build a shareable file');
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const response = await fetchImpl(url, { signal: controller.signal });

        if (!response.ok) {
            throw new Error(`${url} responded ${response.status}`);
        }

        const blob = await response.blob();

        // The MIME type travels with the file, not with the extension: it is
        // what the operating system reads to decide this is a picture rather
        // than an unknown attachment.
        return new FileImpl([blob], fileName, { type });
    } finally {
        clearTimeout(timer);
    }
}

/**
 * A car's card, in the language on screen.
 *
 * `portrait` is the default because this is the file one person hands another,
 * and 4:5 is what a story and a message bubble render whole. `landscape` is the
 * same handover aimed at a feed post; the `unfurl` shape exists for the document
 * head and is never fetched by the app.
 */
export async function fetchCarShareCardFile({ carId, name, languageCode, shape = 'portrait' }, options = {}) {
    return fetchShareImageFile(
        {
            url: carCardPath(carId, languageCode, shape),
            fileName: carPictureFileName(name),
            type: 'image/jpeg',
        },
        options,
    );
}

/** The app's own invitation card. */
export async function fetchAppShareCardFile({ languageCode, shape = 'portrait' }, options = {}) {
    return fetchShareImageFile(
        {
            url: appShareCardPath(languageCode, shape),
            fileName: appShareCardFileName(),
            type: 'image/jpeg',
        },
        options,
    );
}
